import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { BaseViewModel } from '../../../../../models/base/base-view-model';
import { RouteUtils } from '../../../../../utils/route-utils';
import type { NavItem } from '../models/nav-item';
import type { LoadingOptions } from '../../../../../models/shared/loading-options';

@Injectable()
export class NavItemViewModel extends BaseViewModel {
  constructor(protected router: Router) {
    super();
  }

  protected loadingOpts = new BehaviorSubject<LoadingOptions>(null);
  connectToLoadingOpts(loadingOpts: LoadingOptions) {
    this.loadingOpts.next(loadingOpts);
  }

  protected readonly _currentNavUrl = new BehaviorSubject<string>('');
  public readonly currentNavUrl$ = this._currentNavUrl.pipe(distinctUntilChanged());
  connectToCurrentNavUrl(url: string) {
    this._currentNavUrl.next(url);
  }

  protected readonly _navItem = new BehaviorSubject<NavItem>(null);
  public readonly navItem$ = this._navItem as Observable<NavItem>;
  connectToNavItem(item: NavItem) {
    this._navItem.next(item);
  }

  public readonly isNavItemActive$ = combineLatest([this.navItem$, this.currentNavUrl$]).pipe(
    tap(([nav, url]) => {
      const routeWithoutFragment = RouteUtils.cleanFragments(url);
      const currentUrlExactlyMatchesNavItemUrl = routeWithoutFragment === nav?.routerPath;
      if (currentUrlExactlyMatchesNavItemUrl) {
        nav?.saveSubPosition(null, RouteUtils.getFragmentFromUrl(url));
        this.navigateToFirstChild();
      }
    }),
    // this determines if the side nav should appear "open", because you are on a route encapsulated by this nav item
    map(([nav, url]) => url?.indexOf(`${nav?.routerPath}`) === 0),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly multiLineNavItem$ = this.navItem$.pipe(map(navItem => navItem?.name?.length > 12));

  protected navigateToFirstChild() {
    const currentNavItem = this._navItem.getValue();
    const currentUrl = this._currentNavUrl.getValue();
    const forwardSlashCount = currentUrl.split('/').length;
    // Tests if we are on a parent route, ie: /menus
    // This is an invalid route so we should reroute us to the first child nav route, ie: /menus/digital
    // first case catches /menus, second case catches /menus/
    const isParentRoute = forwardSlashCount - 1 === 1 || (forwardSlashCount - 1 === 2 && currentUrl.endsWith('/'));
    if (currentNavItem?.hasSubItems() && isParentRoute) {
      const subNavUrl = currentNavItem?.subItems?.firstOrNull()?.buildFullRouterPath(currentNavItem);
      this.router.navigate([subNavUrl]).then(() => {});
    }
  }
}
